<template>
  <div class="login-container">
    <!-- Background Decor -->
    <div class="decor-circle decor-1"></div>
    <div class="decor-circle decor-2"></div>

    <div class="login-content animate-fade-up">
      <div class="login-header">
        <div class="logo-container">
          <div class="logo-glow"></div>
          <img src="@/assets/logo.png" alt="Logo" class="logo"/>
        </div>
        <h1 class="title">Secure Login</h1>
        <p class="subtitle">Access your account to manage orders</p>
      </div>

      <div class="login-card">
        <div class="input-section">
          <div class="custom-input">
            <label>Account ID</label>
            <div class="field-wrapper">
              <van-icon name="user-o" class="field-icon" />
              <van-field
                  v-model="username"
                  placeholder="Enter your account"
                  :border="false"
                  class="inner-field"
              />
            </div>
            <div v-if="emailError" class="error-hint">{{ emailError }}</div>
          </div>

          <div class="custom-input">
            <label>Security Password</label>
            <div class="field-wrapper">
              <van-icon name="lock" class="field-icon" />
              <van-field
                  v-model="password"
                  placeholder="Enter your password"
                  :type="showPassword ? 'text' : 'password'"
                  :border="false"
                  class="inner-field"
              >
                <template #button>
                  <van-icon 
                    :name="showPassword ? 'eye-o' : 'closed-eye'" 
                    @click="togglePasswordVisibility"
                    class="password-toggle"
                  />
                </template>
              </van-field>
            </div>
            <div v-if="passwordError" class="error-hint">{{ passwordError }}</div>
          </div>
        </div>

        <div class="utilities">
          <van-checkbox v-model="rememberPassword" shape="square" checked-color="#ff9f00">
            Keep me logged in
          </van-checkbox>
        </div>

        <van-button
            :loading="loading"
            loading-text="Authenticating..."
            @click="handleLogin"
            class="login-btn-premium"
            block
        >
          Sign In
        </van-button>
        
        <div class="footer-note">
          By signing in, you agree to our <span>Terms of Service</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import { getTips, login } from '@/utils/api'
import { PASSWORD_MIN_LENGTH_MSG } from '@/utils/passwdStrength'

const router = useRouter()
const username = ref('')
const password = ref('')
const loading = ref(false)
const emailError = ref('')
const passwordError = ref('')
const showPassword = ref(false)
const rememberPassword = ref(true)

const simpleEncrypt = (text) => btoa(encodeURIComponent(text))
const simpleDecrypt = (encrypted) => {
  try {
    return decodeURIComponent(atob(encrypted))
  } catch (e) {
    return ''
  }
}

const checkSavedCredentials = () => {
  const savedUsername = localStorage.getItem('savedUsername')
  const savedPassword = localStorage.getItem('savedPassword')
  if (savedUsername && savedPassword) {
    username.value = savedUsername
    password.value = simpleDecrypt(savedPassword)
    rememberPassword.value = true
  }
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const handleLogin = async () => {
  if (!username.value) {
    showToast('Please enter account')
    return
  }
  if (password.value.length < 6) {
    passwordError.value = PASSWORD_MIN_LENGTH_MSG
    return
  }

  loading.value = true
  try {
    const response = await login({
      username: username.value,
      password: password.value,
    })
    if (response.token) {
      if (rememberPassword.value) {
        localStorage.setItem('savedUsername', username.value)
        localStorage.setItem('savedPassword', simpleEncrypt(password.value))
      } else {
        localStorage.removeItem('savedUsername')
        localStorage.removeItem('savedPassword')
      }
      localStorage.setItem('token', response.token)
    }
    router.push('/')
  } catch (error) {
    showToast({ type: 'fail', message: getTips(error) })
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  checkSavedCredentials()
})
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  background: #ffffff;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* Background Decorations */
.decor-circle {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 0;
}

.decor-1 {
  width: 300px;
  height: 300px;
  background: rgba(255, 159, 0, 0.15);
  top: -100px;
  right: -50px;
}

.decor-2 {
  width: 250px;
  height: 250px;
  background: rgba(255, 159, 0, 0.1);
  bottom: -50px;
  left: -50px;
}

.login-content {
  width: 100%;
  max-width: 400px;
  z-index: 1;
}

.login-header {
  text-align: center;
  margin-bottom: 40px;
}

.logo-container {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  background: var(--primary-color, #ff9f00);
  border-radius: 20px;
  opacity: 0.2;
  filter: blur(15px);
  animation: pulse 2s infinite;
}

.logo {
  width: 48px;
  height: auto;
  position: relative;
  z-index: 2;
}

.title {
  font-size: 28px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 8px;
  letter-spacing: -1px;
}

.subtitle {
  font-size: 14px;
  color: #8e8e93;
}

.login-card {
  background: #fff;
}

.custom-input {
  margin-bottom: 24px;
}

.custom-input label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-wrapper {
  display: flex;
  align-items: center;
  background: #f8f9fb;
  border: 1.5px solid #f0f0f5;
  border-radius: 16px;
  padding: 0 16px;
  transition: all 0.3s ease;
}

.field-wrapper:focus-within {
  border-color: #ff9f00;
  background: #fff;
  box-shadow: 0 4px 15px rgba(255, 159, 0, 0.08);
}

.field-icon {
  font-size: 20px;
  color: #c7c7cc;
  margin-right: 12px;
}

.inner-field {
  flex: 1;
  background: transparent;
  padding: 14px 0;
}

.password-toggle {
  font-size: 20px;
  color: #8e8e93;
}

.error-hint {
  color: #ff3b30;
  font-size: 12px;
  margin-top: 6px;
  padding-left: 4px;
}

.utilities {
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.van-checkbox__label) {
  font-size: 13px;
  color: #8e8e93;
}

.login-btn-premium {
  height: 56px;
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  border: none;
  border-radius: 18px;
  font-size: 17px;
  font-weight: 700;
  color: #000;
  box-shadow: 0 12px 30px rgba(255, 159, 0, 0.3);
}

.footer-note {
  margin-top: 32px;
  text-align: center;
  font-size: 12px;
  color: #c7c7cc;
}

.footer-note span {
  color: #ff9f00;
  font-weight: 600;
}

/* Animations */
@keyframes pulse {
  0% { transform: scale(0.95); opacity: 0.2; }
  50% { transform: scale(1.05); opacity: 0.4; }
  100% { transform: scale(0.95); opacity: 0.2; }
}

.animate-fade-up {
  animation: fadeUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
